/**
 * Typed HTTP client for the `seen-backend` REST API.
 *
 * Every endpoint answers `{ ok: true, data }` or
 * `{ ok: false, error: { code, message, details } }`, so `apiFetch` unwraps
 * `data` and throws a typed `ApiError` otherwise.
 *
 * `VITE_API_BASE_URL` may be given either as the host (`http://localhost:4000`)
 * or with the API prefix already included (`http://localhost:4000/api`) — both
 * resolve to the same base. When it is unset the app runs entirely on the
 * bundled mock data (`USE_MOCK_API`) so the preview keeps working.
 */
import { clearSession, getAccessToken, getCartToken, getRefreshToken, refreshSession } from "./session";

const RAW_BASE = ((import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "").trim();

/** Backend origin without a trailing slash and without the `/api` prefix. */
export const API_ORIGIN = RAW_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");

/** Fully-qualified API base, always ending in `/api`. */
export const API_BASE_URL = API_ORIGIN ? `${API_ORIGIN}/api` : "";

/** True while no backend is configured — the mock implementations are used. */
export const USE_MOCK_API = !API_BASE_URL;

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown[];

  constructor(message: string, code = "REQUEST_FAILED", status = 0, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = Array.isArray(details) ? details : details ? [details] : [];
  }
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** Query string values; `undefined` / `null` / `""` entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Send the bearer token (default: true when a session exists). */
  auth?: boolean;
  signal?: AbortSignal;
  /** Storefront reads: force a language instead of the document language. */
  lang?: string;
}

export function currentLanguage(): string {
  if (typeof document === "undefined") return "en";
  return document.documentElement.lang === "ar" ? "ar" : "en";
}

function buildUrl(path: string, query: ApiRequestOptions["query"], lang: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const [pathname, existing] = normalized.split("?");
  const search = new URLSearchParams(existing ?? "");
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  // `?lang=` wins over Accept-Language server-side and keeps caches per-locale.
  if (!search.has("lang")) search.set("lang", lang);
  const qs = search.toString();
  return `${API_BASE_URL}${pathname}${qs ? `?${qs}` : ""}`;
}

export async function parseError(response: Response): Promise<ApiError> {
  let code = "REQUEST_FAILED";
  let message = `Request failed with status ${response.status}.`;
  let details: unknown;
  try {
    const payload = (await response.json()) as { error?: ApiErrorShape };
    if (payload?.error) {
      code = payload.error.code ?? code;
      message = payload.error.message ?? message;
      details = payload.error.details;
    }
  } catch {
    /* non-JSON error body — keep the generic message */
  }
  return new ApiError(message, code, response.status, details);
}

async function request<T>(
  path: string,
  options: ApiRequestOptions,
  retryOn401: boolean,
): Promise<T> {
  const { method = "GET", body, query, auth = true, signal } = options;
  const lang = options.lang ?? currentLanguage();

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": lang,
    "X-Cart-Token": getCartToken(),
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  // Access tokens live in memory only: after a reload, mint a fresh one from the
  // stored refresh token *before* the first call so nothing 401s on boot.
  let token = getAccessToken();
  if (auth && !token && getRefreshToken()) token = await refreshSession(API_ORIGIN);
  if (auth && token) headers["Authorization"] = `Bearer ${token}`;


  let response: Response;
  try {
    response = await fetch(buildUrl(path, query, lang), {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if ((error as Error)?.name === "AbortError") throw error;
    throw new ApiError("Cannot reach the store right now. Please try again.", "NETWORK_ERROR");
  }

  if (response.status === 401 && retryOn401) {
    const refreshed = await refreshSession(API_ORIGIN);
    if (refreshed) return request<T>(path, options, false);
    clearSession();
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | { ok?: boolean; data?: T; error?: ApiErrorShape }
    | null;

  if (import.meta.env.VITE_ENV !== "production") {
    console.log(`[API ${method}] ${buildUrl(path, query, lang)}`, {
      status: response.status,
      requestBody: body,
      responseData: payload,
    });
  }
  if (payload?.ok === false) {
    throw new ApiError(
      payload.error?.message ?? "Request failed.",
      payload.error?.code,
      response.status,
      payload.error?.details,
    );
  }
  return ((payload?.data ?? payload) as T) ?? (undefined as T);
}

export function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return request<T>(path, options, true);
}

export const api = {
  get: <T,>(path: string, query?: ApiRequestOptions["query"]) => apiFetch<T>(path, { query }),
  post: <T,>(path: string, body?: unknown) => apiFetch<T>(path, { method: "POST", body }),
  patch: <T,>(path: string, body?: unknown) => apiFetch<T>(path, { method: "PATCH", body }),
  put: <T,>(path: string, body?: unknown) => apiFetch<T>(path, { method: "PUT", body }),
  del: <T,>(path: string, body?: unknown) => apiFetch<T>(path, { method: "DELETE", body }),
};


/* -------------------------------------------------------------------------- */
/*  Asset URLs                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Hosts that appear in stored asset URLs but cannot actually serve them:
 * the backend's placeholder `APP_URL` and the retired Supabase storage host.
 */
const STALE_ASSET_HOSTS = [/^api\.yourdomain\.com$/i, /(^|\.)supabase\.co$/i];

/**
 * Rewrites a stored file URL onto the configured backend origin. Relative
 * `/uploads/...` paths are resolved, and absolute URLs pointing at a stale host
 * keep only their path so the real API host serves the file.
 */
export function resolveAssetUrl(source: unknown): string {
  if (!source) return "";

  // Product endpoints may return `images` as URL strings or image records,
  // while Appearance returns a direct imageUrl/image_url. Normalize both.
  const assetValue = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (Array.isArray(value)) return assetValue(value[0]);
    if (typeof value === "object" && value !== null) {
      const record = value as Record<string, unknown>;
      return assetValue(
        record.url ?? record.imageUrl ?? record.image_url ??
        record.mainImageUrl ?? record.main_image_url ?? record.src ?? record.path,
      );
    }
    return "";
  };

  const value = assetValue(source).trim();
  if (!value) return "";
  if (/^(data:|blob:)/i.test(value)) return value;

  // Stored files always live under /uploads on the backend. A bare relative
  // path (e.g. `products/abc.png` or `/products/abc.png`) needs that prefix.
  const toUploadsPath = (p: string) => {
    const clean = `/${p.replace(/^\/+/, "")}`;
    if (/^\/(uploads|api|assets|static|public)\//i.test(clean)) return clean;
    return `/uploads${clean}`;
  };

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      if (API_ORIGIN && STALE_ASSET_HOSTS.some((re) => re.test(parsed.hostname))) {
        return `${API_ORIGIN}${toUploadsPath(parsed.pathname)}${parsed.search}`;
      }
      return value;
    } catch {
      return value;
    }
  }

  const path = toUploadsPath(value);
  return API_ORIGIN ? `${API_ORIGIN}${path}` : path;
}


/* -------------------------------------------------------------------------- */
/*  Uploads — POST /api/uploads (multipart, field name `file`, max 10 MB)      */
/* -------------------------------------------------------------------------- */

export interface UploadedAsset {
  url: string;

  path: string;
  folder?: string;
  filename?: string;
  size?: number;
  mime?: string;
  replaced?: string | null;
}

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const ACCEPTED_UPLOAD_MIMES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "audio/webm",
  "audio/mp4",
];

/**
 * Uploads a file and resolves only once the backend confirms a stored URL.
 * Throws a typed `ApiError` on every failure path so callers never report a
 * success that did not happen.
 */
export async function uploadAsset(
  file: File | Blob,
  options: { folder?: string; replace?: string | null; filename?: string } = {},
): Promise<UploadedAsset> {
  if (!file || !(file as Blob).size) {
    throw new ApiError("Choose a file to upload.", "BAD_REQUEST", 400);
  }
  if ((file as Blob).size > MAX_UPLOAD_BYTES) {
    throw new ApiError("File is larger than the 10 MB limit.", "FILE_TOO_LARGE", 413);
  }
  const mime = (file as Blob).type;
  if (mime && !ACCEPTED_UPLOAD_MIMES.includes(mime)) {
    throw new ApiError(`Unsupported file type: ${mime}`, "BAD_REQUEST", 400);
  }

  const name =
    options.filename ?? (file instanceof File && file.name ? file.name : "upload.bin");

  const form = new FormData();
  form.append("file", file, name);
  if (options.folder) form.append("folder", options.folder);
  if (options.replace) form.append("replace", options.replace);

  const token = getAccessToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/uploads`, { method: "POST", headers, body: form });
  } catch {
    throw new ApiError("Upload failed — the server is unreachable.", "NETWORK_ERROR");
  }

  if (response.status === 401 && (await refreshSession(API_ORIGIN))) {
    return uploadAsset(file, options);
  }
  if (!response.ok) throw await parseError(response);

  const payload = (await response.json().catch(() => null)) as
    | { ok?: boolean; data?: UploadedAsset; error?: ApiErrorShape }
    | null;
  if (payload?.ok === false) {
    throw new ApiError(
      payload.error?.message ?? "Upload failed.",
      payload.error?.code ?? "UPLOAD_FAILED",
      response.status,
      payload.error?.details,
    );
  }
  const asset = (payload?.data ?? payload) as UploadedAsset | null;
  // Prefer the server-provided absolute URL; `path` is a storage path, not a URL.
  const bestUrl = asset?.url || asset?.path;
  if (!bestUrl) {
    throw new ApiError("Upload failed — the server returned no file URL.", "UPLOAD_FAILED");
  }
  // Normalize the returned URL onto the real API origin so previews resolve.
  return { ...asset, url: resolveAssetUrl(bestUrl), path: asset?.path ?? asset?.url };

}

/** DELETE /api/uploads — removes a stored asset by URL or path (admin only). */
export async function deleteAsset(urlOrPath: string): Promise<void> {
  if (!urlOrPath) return;
  await apiFetch<void>("/uploads", { method: "DELETE", body: { url: urlOrPath } });
}

/** Back-compat helper: single-file upload returning `{ url, filename }`. */
export async function uploadFile(
  file: File,
  folder?: string,
): Promise<{ url: string; filename: string }> {
  const asset = await uploadAsset(file, { folder });
  return { url: asset.url, filename: asset.filename ?? asset.path };
}

/** Fire-and-forget analytics ping — never rejects. */
export function trackVisitRemote(path: string, sessionId?: string): void {
  if (USE_MOCK_API) return;
  void apiFetch("/catalog/visits", {
    method: "POST",
    body: {
      path,
      referrer: typeof document === "undefined" ? "" : document.referrer,
      ...(sessionId ? { sessionId } : {}),
    },
  }).catch(() => undefined);
}

/** GET /api/health — outside the envelope on purpose. */
export async function healthCheck(): Promise<{ ok: boolean; db_ok?: boolean }> {
  if (USE_MOCK_API) return { ok: true, db_ok: true };
  const response = await fetch(`${API_BASE_URL}/health`, {
    headers: { Accept: "application/json" },
  });
  return (await response.json()) as { ok: boolean; db_ok?: boolean };
}
